import { useState, useEffect, useCallback } from "react";
import ERPLayout from "@/components/feature/ERPLayout";
import { mockRoles } from "@/mocks/admin";

export interface AdminUser {
  id: number;
  empId: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  roleId: number;
  department: string;
  status: "Active" | "Inactive" | "Suspended";
  lastLogin: string;
  createdAt: string;
  avatar: string;
  twoFA: boolean;
  loginCount: number;
}
import { adminApi } from "@/services/api";

// ── Created User Modal (shows temp password) ──────────────────────────────────
interface CreatedUserModalProps {
  user: AdminUser;
  tempPassword: string;
  onClose: () => void;
}

function CreatedUserModal({ user, tempPassword, onClose }: CreatedUserModalProps) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-amber-600">User Created Successfully</h3>
            <p className="text-xs text-gray-500 mt-0.5">Share this temporary password with the user.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* User info */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: "#f59e0b" }}>
              {user.avatar}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">{user.name}</p>
              <p className="text-xs text-gray-500">{user.email} · {user.role}</p>
            </div>
          </div>

          {/* Temp password */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-600">Temporary Password</label>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={tempPassword}
                className="flex-1 bg-gray-100 border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-mono text-gray-800"
              />
              <button
                onClick={copyToClipboard}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-xl text-sm font-medium cursor-pointer transition-all whitespace-nowrap"
              >
                {copied ? <><i className="ri-check-line mr-1" />Copied</> : <><i className="ri-file-copy-line mr-1" />Copy</>}
              </button>
            </div>
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <i className="ri-alert-line" />
              This password will only be shown once. Make sure to save it.
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-full text-white rounded-xl py-2.5 text-sm font-bold cursor-pointer transition-all whitespace-nowrap"
            style={{ background: "#7c3aed" }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete User Modal ─────────────────────────────────────────────────────────
interface DeleteUserModalProps {
  user: AdminUser;
  onClose: () => void;
  onConfirm: () => void;
}

function DeleteUserModal({ user, onClose, onConfirm }: DeleteUserModalProps) {
  const [confirmText, setConfirmText] = useState("");
  const canDelete = confirmText === user.email;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-red-600">Delete User</h3>
            <p className="text-xs text-gray-500 mt-0.5">This action cannot be undone.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* User info */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-red-50 border border-red-100">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: "#ef4444" }}>
              {user.avatar}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">{user.name}</p>
              <p className="text-xs text-gray-500">{user.email} · {user.role}</p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-gray-700">
              To confirm deletion, type the user's email address:
            </p>
            <code className="block bg-gray-100 px-3 py-2 rounded-lg text-sm font-mono text-gray-800">{user.email}</code>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Enter email to confirm"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400"
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2.5 text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!canDelete}
              className="flex-1 bg-red-500 text-white rounded-xl py-2.5 text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-delete-bin-line mr-1" />Delete User
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Reset Password Modal ──────────────────────────────────────────────────────
interface ResetPwModalProps {
  user: AdminUser;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

function ResetPasswordModal({ user, onClose, onSuccess }: ResetPwModalProps) {
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const pwStrength = (pw: string) => {
    if (!pw) return { score: 0, label: "", color: "" };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { score, label: "Weak", color: "#dc2626" };
    if (score <= 3) return { score, label: "Fair", color: "#d97706" };
    return { score, label: "Strong", color: "#16a34a" };
  };

  const strength = pwStrength(newPw);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (!/[A-Z]/.test(newPw)) { setError("Password must contain at least one uppercase letter."); return; }
    if (!/[0-9]/.test(newPw)) { setError("Password must contain at least one number."); return; }
    if (newPw !== confirmPw) { setError("Passwords do not match."); return; }
    setSaving(true);
    try {
      await adminApi.resetUserPassword(user.id, newPw);
      onSuccess(`Password reset for ${user.name}`);
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to reset password";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-900">Reset Password</h3>
            <p className="text-xs text-gray-500 mt-0.5">Set a new password for <span className="font-semibold text-gray-700">{user.name}</span></p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* User info */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: "#7c3aed" }}>
              {user.avatar}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">{user.name}</p>
              <p className="text-xs text-gray-500">{user.email} · {user.role}</p>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl border border-red-100">
              <i className="ri-error-warning-line text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* New password */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">New Password</label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="Min 8 chars, 1 uppercase, 1 number"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className={showNew ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
              </button>
            </div>
            {newPw && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex-1 h-1 rounded-full transition-all" style={{ background: i <= strength.score ? strength.color : "#e5e7eb" }} />
                  ))}
                </div>
                <p className="text-xs font-medium" style={{ color: strength.color }}>{strength.label}</p>
              </div>
            )}
          </div>

          {/* Confirm password */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                placeholder="Re-enter new password"
                className={`w-full border rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 transition-all ${
                  confirmPw && confirmPw !== newPw ? "border-red-300 focus:border-red-400" : "border-gray-200 focus:border-violet-400"
                }`}
              />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className={showConfirm ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
              </button>
            </div>
            {confirmPw && confirmPw !== newPw && <p className="text-xs text-red-500 mt-1">Passwords do not match</p>}
            {confirmPw && confirmPw === newPw && newPw && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><i className="ri-checkbox-circle-line" /> Passwords match</p>
            )}
          </div>

          {/* Requirements */}
          <div className="p-3 rounded-xl bg-gray-50 border border-gray-100 space-y-1.5">
            {[
              { rule: "At least 8 characters", check: newPw.length >= 8 },
              { rule: "One uppercase letter (A-Z)", check: /[A-Z]/.test(newPw) },
              { rule: "One number (0-9)", check: /[0-9]/.test(newPw) },
            ].map((r) => (
              <div key={r.rule} className="flex items-center gap-2">
                <i className={`text-sm flex-shrink-0 ${r.check && newPw ? "ri-checkbox-circle-fill text-amber-500" : "ri-checkbox-blank-circle-line text-gray-300"}`} />
                <span className={`text-xs ${r.check && newPw ? "text-amber-700" : "text-gray-500"}`}>{r.rule}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2.5 text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 text-white rounded-xl py-2.5 text-sm font-bold cursor-pointer transition-all whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: "#7c3aed" }}
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin" />Resetting...</>
              ) : (
                <><i className="ri-lock-password-line" />Reset Password</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

type StatusFilter = "all" | "Active" | "Inactive" | "Suspended";

const ALL_SKILLS = [
  "Total Station", "DGPS", "Levelling", "AutoCAD", "Civil 3D",
  "Boundary Survey", "GPS", "Drone Survey", "GIS", "QGIS", "ArcGIS",
  "Revenue Survey", "Hydrogeological Survey", "As-Built Survey",
  "CAD Drafting", "KMZ/KML", "Shapefile", "Python", "Pix4D",
  "Orthophoto", "Point Cloud", "Soil Testing", "Lab Analysis",
  "Report Writing", "Project Management", "Reporting",
];

interface UserModalProps {
  user?: AdminUser | null;
  onClose: () => void;
  onSave: (u: Partial<AdminUser> & { skills?: string[] }) => Promise<void>;
}

function UserModal({ user, onClose, onSave }: UserModalProps) {
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    role: user?.role || mockRoles[2].name,
    department: user?.department || "Office",
    status: user?.status || "Active",
    twoFA: user?.twoFA || false,
  });
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [customSkill, setCustomSkill] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggleSkill = (skill: string) => {
    setSelectedSkills((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    );
  };

  const addCustomSkill = () => {
    const trimmed = customSkill.trim();
    if (trimmed && !selectedSkills.includes(trimmed)) {
      setSelectedSkills((prev) => [...prev, trimmed]);
    }
    setCustomSkill("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const roleObj = mockRoles.find((r) => r.name === form.role);
      await onSave({
        ...form,
        roleId: roleObj?.id || 3,
        avatar: form.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase(),
        skills: selectedSkills,
      });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-bold text-gray-900">{user ? "Edit User" : "Add New User"}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl border border-red-100">
              <i className="ri-error-warning-line text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Full Name</label>
              <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Ravi Teja" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="user@cadsoft.in" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone</label>
              <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91 98001 XXXXX" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 cursor-pointer">
                {mockRoles.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Department</label>
              <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 cursor-pointer">
                {["Management", "Field Operations", "Office", "Lab", "External"].map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as AdminUser["status"] })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 cursor-pointer">
                {["Active", "Inactive", "Suspended"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-span-2 flex items-center gap-3 p-3 rounded-xl bg-gray-50">
              <input type="checkbox" id="twofa" checked={form.twoFA} onChange={(e) => setForm({ ...form, twoFA: e.target.checked })} className="w-4 h-4 accent-violet-600 cursor-pointer" />
              <label htmlFor="twofa" className="text-sm font-medium text-gray-700 cursor-pointer">Enable Two-Factor Authentication (2FA)</label>
            </div>
          </div>

          {/* Skills Selection */}
          <div className="border-t border-gray-100 pt-4">
            <label className="text-xs font-semibold text-gray-600 mb-2 block flex items-center gap-1.5">
              <i className="ri-tools-line text-violet-500" />
              Skills &amp; Expertise
            </label>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {ALL_SKILLS.map((skill) => {
                const isSelected = selectedSkills.includes(skill);
                return (
                  <button
                    key={skill}
                    type="button"
                    onClick={() => toggleSkill(skill)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                      isSelected
                        ? "bg-violet-100 border-violet-300 text-violet-700"
                        : "bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    {isSelected && <i className="ri-check-line mr-1 text-xs" />}
                    {skill}
                  </button>
                );
              })}
            </div>
            {/* Custom skill input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={customSkill}
                onChange={(e) => setCustomSkill(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomSkill(); } }}
                placeholder="Add custom skill..."
                className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              />
              <button
                type="button"
                onClick={addCustomSkill}
                disabled={!customSkill.trim()}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-xl text-sm font-medium cursor-pointer transition-all whitespace-nowrap disabled:opacity-50"
              >
                <i className="ri-add-line" /> Add
              </button>
            </div>
            {selectedSkills.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {selectedSkills.map((skill) => (
                  <span key={skill} className="flex items-center gap-1 text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">
                    {skill}
                    <button type="button" onClick={() => toggleSkill(skill)} className="w-3 h-3 flex items-center justify-center hover:text-red-500 cursor-pointer">
                      <i className="ri-close-line text-xs" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={saving} className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2.5 text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 text-white rounded-xl py-2.5 text-sm font-bold cursor-pointer transition-all whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "#7c3aed" }}>
              {saving ? (
                <><i className="ri-loader-4-line animate-spin" />{user ? "Saving..." : "Creating..."}</>
              ) : (
                user ? "Save Changes" : "Create User"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const statusConfig = {
  Active: { color: "#10b981", bg: "#ecfdf5" },
  Inactive: { color: "#64748b", bg: "#f8fafc" },
  Suspended: { color: "#ef4444", bg: "#fef2f2" },
};

const roleColors: Record<string, string> = {
  "Super Admin": "#ef4444",
  "Survey Manager": "#f59e0b",
  "Office Staff": "#3b82f6",
  "Field Surveyor": "#10b981",
  "Lab Analyst": "#8b5cf6",
  "Client Portal User": "#64748b",
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [resetPwUser, setResetPwUser] = useState<AdminUser | null>(null);
  const [deleteUser, setDeleteUser] = useState<AdminUser | null>(null);
  const [createdUser, setCreatedUser] = useState<{ user: AdminUser; tempPassword: string } | null>(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<"created" | "name" | "lastLogin">("created");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const showSuccess = (msg: string) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(""), 3000); };

  // Normalize DB status (lowercase) to display format (title case)
  const normalizeStatusDisplay = (s: string): AdminUser['status'] => {
    const lower = (s || 'active').toLowerCase();
    if (lower === 'inactive') return 'Inactive';
    if (lower === 'suspended') return 'Suspended';
    return 'Active';
  };

  // Map DB user row to AdminUser format
  const mapDbUser = (u: Record<string, unknown>): AdminUser => ({
    id: (u.id as number) || (u.id as string) || 0,
    empId: (u.emp_id as string) || (u.empId as string) || '',
    name: (u.name as string) || '',
    email: (u.email as string) || '',
    phone: (u.phone as string) || '',
    role: (u.role as string) || 'Office Staff',
    roleId: (u.role_id as number) || (u.roleId as number) || 3,
    department: (u.department as string) || 'Office',
    status: normalizeStatusDisplay((u.status as string) || 'active'),
    lastLogin: (u.last_login as string) || (u.lastLogin as string) || 'Never',
    loginCount: (u.login_count as number) || (u.loginCount as number) || 0,
    createdAt: (u.created_at as string) || (u.createdAt as string) || new Date().toISOString().split('T')[0],
    avatar: (u.avatar as string) || ((u.name as string) || '??').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase(),
    twoFA: (u.two_fa as number) === 1 || (u.twoFA as boolean) || false,
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getUsers();
      // Backend returns { data: [...] } or { users: [...] } or just the array
      const raw = data?.data || data?.users || data || [];
      if (Array.isArray(raw)) {
        setUsers(raw.map(mapDbUser));
      }
    } catch {
      // keep mock data if API fails
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filtered = users
    .filter((u) => {
      if (statusFilter !== "all" && u.status !== statusFilter) return false;
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (search && !u.name.toLowerCase().includes(search.toLowerCase()) && !u.email.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortBy === "created") {
        cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      } else if (sortBy === "name") {
        cmp = a.name.localeCompare(b.name);
      } else if (sortBy === "lastLogin") {
        const aTime = a.lastLogin === "Never" ? 0 : new Date(a.lastLogin).getTime();
        const bTime = b.lastLogin === "Never" ? 0 : new Date(b.lastLogin).getTime();
        cmp = aTime - bTime;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

  const handleSave = async (data: Partial<AdminUser> & { skills?: string[] }) => {
    if (editUser) {
      // Optimistic update
      const previous = users.find((u) => u.id === editUser.id);
      setUsers((prev) => prev.map((u) => u.id === editUser.id ? { ...u, ...data } : u));
      try {
        const updated = await adminApi.updateUser(editUser.id, {
          name: data.name,
          email: data.email,
          phone: data.phone,
          role: data.role,
          department: data.department,
          status: data.status,
          twoFA: data.twoFA,
        });
        // Sync with real DB response
        setUsers((prev) => prev.map((u) => u.id === editUser.id ? mapDbUser(updated) : u));
        showSuccess("User updated successfully");
        setEditUser(null);
      } catch (err: unknown) {
        // Rollback on failure
        if (previous) setUsers((prev) => prev.map((u) => u.id === editUser.id ? previous : u));
        throw err; // re-throw so UserModal shows the error
      }
    } else {
      // Create new user
      try {
        const res = await adminApi.createUser({
          name: data.name,
          email: data.email,
          phone: data.phone,
          role: data.role,
          department: data.department,
          status: data.status,
          skills: data.skills || [],
        });
        const created = mapDbUser(res);
        setUsers((prev) => [created, ...prev]);
        // Show temp password modal if backend returned one
        if (res.tempPassword) {
          setCreatedUser({ user: created, tempPassword: res.tempPassword });
        } else {
          showSuccess("User created successfully");
        }
        await fetchUsers(); // refresh with real data
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to create user';
        showSuccess(`Error: ${msg}`);
      }
      setShowModal(false);
    }
  };

  const handleStatusChange = async (id: number, status: AdminUser["status"]) => {
    setUsers((prev) => prev.map((u) => u.id === id ? { ...u, status } : u));
    try {
      await adminApi.updateUserStatus(id, status);
    } catch {
      // keep optimistic
    }
    showSuccess(`User ${status.toLowerCase()}`);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUser) return;
    const target = deleteUser;
    // Optimistic removal
    setUsers((prev) => prev.filter((u) => u.id !== target.id));
    setDeleteUser(null);
    try {
      await adminApi.deleteUser(target.id);
      showSuccess(`User "${target.name}" deleted`);
    } catch (err: unknown) {
      // Rollback on failure
      setUsers((prev) => [target, ...prev]);
      const msg = err instanceof Error ? err.message : 'Failed to delete user';
      showSuccess(`Error: ${msg}`);
    }
  };

  return (
    <ERPLayout title="User Management" subtitle="Manage system users, roles, and access control">
      {successMsg && (
        <div className="fixed top-4 right-4 z-50 text-white text-sm px-4 py-2 rounded-xl font-medium" style={{ background: "#7c3aed" }}>
          <i className="ri-checkbox-circle-line mr-2" />{successMsg}
        </div>
      )}
      {(showModal || editUser) && (
        <UserModal
          user={editUser}
          onClose={() => { setShowModal(false); setEditUser(null); }}
          onSave={async (data) => {
            await handleSave(data);
            setShowModal(false);
          }}
        />
      )}
      {resetPwUser && (
        <ResetPasswordModal
          user={resetPwUser}
          onClose={() => setResetPwUser(null)}
          onSuccess={(msg) => { showSuccess(msg); setResetPwUser(null); }}
        />
      )}
      {deleteUser && (
        <DeleteUserModal
          user={deleteUser}
          onClose={() => setDeleteUser(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
      {createdUser && (
        <CreatedUserModal
          user={createdUser.user}
          tempPassword={createdUser.tempPassword}
          onClose={() => setCreatedUser(null)}
        />
      )}

      <div className="p-6 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Users", value: users.length, color: "#7c3aed", bg: "#f5f3ff" },
            { label: "Active", value: users.filter((u) => u.status === "Active").length, color: "#f59e0b", bg: "#fffbeb" },
            { label: "Inactive", value: users.filter((u) => u.status === "Inactive").length, color: "#64748b", bg: "#f8fafc" },
            { label: "Suspended", value: users.filter((u) => u.status === "Suspended").length, color: "#ef4444", bg: "#fef2f2" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
              <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
              <p className="text-sm text-gray-600">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters + Add */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-sm" />
            </span>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 cursor-pointer">
            <option value="all">All Status</option>
            {["Active", "Inactive", "Suspended"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 cursor-pointer">
            <option value="all">All Roles</option>
            {mockRoles.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
          </select>
          <button
            onClick={fetchUsers}
            className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`} />
            </span>
            Refresh
          </button>
          <button onClick={() => { setEditUser(null); setShowModal(true); }}
            className="flex items-center gap-2 text-white px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap" style={{ background: "#7c3aed" }}>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-sm" /></span>
            Add User
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-700">{filtered.length} user(s)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  {[
                    { key: "name", label: "User", sortable: true },
                    { key: null, label: "Role", sortable: false },
                    { key: null, label: "Department", sortable: false },
                    { key: null, label: "Status", sortable: false },
                    { key: "created", label: "Created", sortable: true },
                    { key: "lastLogin", label: "Last Login", sortable: true },
                    { key: null, label: "2FA", sortable: false },
                    { key: null, label: "Logins", sortable: false },
                    { key: null, label: "Actions", sortable: false },
                  ].map((h) => (
                    <th
                      key={h.label}
                      onClick={() => {
                        if (!h.sortable) return;
                        if (sortBy === h.key) {
                          setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                        } else {
                          setSortBy(h.key as typeof sortBy);
                          setSortDir("desc");
                        }
                      }}
                      className={`text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap ${h.sortable ? "cursor-pointer hover:text-violet-600 select-none" : ""}`}
                    >
                      <div className="flex items-center gap-1">
                        {h.label}
                        {h.sortable && sortBy === h.key && (
                          <i className={sortDir === "asc" ? "ri-arrow-up-s-line text-violet-600" : "ri-arrow-down-s-line text-violet-600"} />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((u) => {
                  const stCfg = statusConfig[u.status] || statusConfig.Inactive;
                  const roleColor = roleColors[u.role] || "#64748b";
                  return (
                    <tr key={u.id} className="hover:bg-gray-50 transition-all group">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: "#7c3aed" }}>
                            {u.avatar}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{u.name}</p>
                            <p className="text-xs text-gray-400">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ color: roleColor, background: roleColor + "18" }}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs text-gray-600 whitespace-nowrap">{u.department}</td>
                      <td className="px-5 py-3">
                        <select
                          value={u.status}
                          onChange={(e) => handleStatusChange(u.id, e.target.value as AdminUser["status"])}
                          className="text-xs px-2 py-1 rounded-lg border-0 font-semibold cursor-pointer focus:outline-none"
                          style={{ color: stCfg.color, background: stCfg.bg }}
                        >
                          {["Active", "Inactive", "Suspended"].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-5 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {(() => {
                          try {
                            const d = new Date(u.createdAt);
                            if (isNaN(d.getTime())) return "—";
                            return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
                          } catch { return "—"; }
                        })()}
                      </td>
                      <td className="px-5 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {u.lastLogin === "Never" || !u.lastLogin ? (
                          "Never"
                        ) : (
                          (() => {
                            try {
                              const d = new Date(u.lastLogin);
                              if (isNaN(d.getTime())) return "Invalid date";
                              return d.toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              });
                            } catch {
                              return String(u.lastLogin);
                            }
                          })()
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${u.twoFA ? "bg-amber-50 text-amber-600" : "bg-gray-100 text-gray-400"}`}>
                          {u.twoFA ? "ON" : "OFF"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs font-semibold text-gray-700">{u.loginCount ?? 0}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setEditUser(u)}
                            title="Edit user"
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-violet-50 cursor-pointer transition-all" style={{ color: "#7c3aed" }}>
                            <i className="ri-edit-line text-sm" />
                          </button>
                          <button
                            onClick={() => setResetPwUser(u)}
                            title="Reset password"
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-amber-50 cursor-pointer text-amber-500 transition-all">
                            <i className="ri-lock-password-line text-sm" />
                          </button>
                          <button
                            onClick={() => setDeleteUser(u)}
                            title="Delete user"
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer text-red-400 transition-all">
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <i className="ri-user-search-line text-4xl block mb-2" />
                <p className="text-sm">No users match your filters</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </ERPLayout>
  );
}
